import React, { useMemo, useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import ConsequenceReveal from '../shared/ConsequenceReveal';
import styles from './DilemmaRoomEngine.module.css';

const DilemmaRoomEngine = ({ gamifiedQuestion, onAnswer, accent = '#e63946' }) => {
  const [typingDone, setTypingDone] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showOutcome, setShowOutcome] = useState(false);
  const [panelStage, setPanelStage] = useState(0);
  const [choicesRevealed, setChoicesRevealed] = useState(false);

  const options = useMemo(() => gamifiedQuestion?.options?.slice(0, 4) || [], [gamifiedQuestion?.options]);
  const selectedOption = options.find((option) => option.label === selected);

  // Panel sequence animation
  useEffect(() => {
    const timers = [
      setTimeout(() => setPanelStage(1), 100),
      setTimeout(() => setPanelStage(2), 700),
      setTimeout(() => setPanelStage(3), 1300),
      setTimeout(() => {
        setChoicesRevealed(true);
        setTypingDone(true);
      }, 1900)
    ];
    return () => timers.forEach(clearTimeout);
  }, []);

  const handleSelect = (label) => {
    if (!typingDone || selected) return;
    setSelected(label);
    setTimeout(() => {
      setShowOutcome(true);
    }, 800);
  };

  // Parse scene elements from question data
  const sceneElements = gamifiedQuestion?.scene?.elements || {};
  const panel1Type = sceneElements.panel1?.type || 'teacher_whiteboard';
  const panel2Type = sceneElements.panel2?.type || 'student_confused';

  return (
    <div className={styles.engine} style={{ '--accent': accent }}>
      {/* SCENE AREA - Three panels */}
      <div className={styles.sceneArea}>
        <div className={styles.panelsContainer}>
          {/* Panel 1 - Setup/Context */}
          <motion.div
            className={styles.panel}
            initial={{ x: 100, opacity: 0 }}
            animate={{ x: panelStage >= 1 ? 0 : 100, opacity: panelStage >= 1 ? 1 : 0 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          >
            <svg viewBox="0 0 180 220" className={styles.panelSvg}>
              {/* Panel border with hand-drawn style */}
              <rect x="5" y="5" width="170" height="210" rx="8" fill="#fff" stroke="#1a1a1a" strokeWidth="2.5" />

              {panel1Type.includes('whiteboard') ? (
                // Teacher at whiteboard
                <g>
                  {/* Whiteboard */}
                  <rect x="30" y="30" width="120" height="80" rx="4" fill="#f8f9fa" stroke="#1a1a1a" strokeWidth="2" />
                  {/* Pyramid diagram */}
                  <polygon points="90,45 60,100 120,100" fill="none" stroke="#2a9d8f" strokeWidth="2" />
                  <line x1="75" y1="72" x2="105" y2="72" stroke="#e63946" strokeWidth="2" />
                  <line x1="70" y1="85" x2="110" y2="85" stroke="#f4a261" strokeWidth="2" />
                  {/* Teacher figure */}
                  <circle cx="90" cy="140" r="12" fill="#fff" stroke="#1a1a1a" strokeWidth="2" />
                  <line x1="90" y1="152" x2="90" y2="185" stroke="#1a1a1a" strokeWidth="2" />
                  <line x1="75" y1="165" x2="105" y2="165" stroke="#1a1a1a" strokeWidth="2" />
                </g>
              ) : panel1Type.includes('manual') ? (
                // Reading policy manual
                <g>
                  {/* Document */}
                  <rect x="50" y="40" width="80" height="100" rx="2" fill="#fff" stroke="#1a1a1a" strokeWidth="2" />
                  <line x1="60" y1="60" x2="120" y2="60" stroke="#e0e0e0" strokeWidth="2" />
                  <line x1="60" y1="75" x2="120" y2="75" stroke="#e0e0e0" strokeWidth="2" />
                  <line x1="60" y1="90" x2="100" y2="90" stroke="#e0e0e0" strokeWidth="2" />
                  {/* Seal */}
                  <circle cx="110" cy="115" r="12" fill="#e63946" opacity="0.3" />
                  {/* Reader */}
                  <circle cx="90" cy="165" r="10" fill="#fff" stroke="#1a1a1a" strokeWidth="2" />
                  <line x1="90" y1="175" x2="90" y2="200" stroke="#1a1a1a" strokeWidth="2" />
                </g>
              ) : (
                // Default scene
                <g>
                  <circle cx="90" cy="80" r="25" fill="#f0f0f0" stroke="#1a1a1a" strokeWidth="2" />
                  <text x="90" y="88" textAnchor="middle" fontSize="20">📋</text>
                  <circle cx="90" cy="160" r="12" fill="#fff" stroke="#1a1a1a" strokeWidth="2" />
                </g>
              )}
            </svg>
            <span className={styles.panelCaption}>{sceneElements.panel1?.caption || 'Context'}</span>
          </motion.div>

          {/* Panel 2 - Problem/Dilemma */}
          <motion.div
            className={styles.panel}
            initial={{ x: 100, opacity: 0 }}
            animate={{ x: panelStage >= 2 ? 0 : 100, opacity: panelStage >= 2 ? 1 : 0 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          >
            <svg viewBox="0 0 180 220" className={styles.panelSvg}>
              <rect x="5" y="5" width="170" height="210" rx="8" fill="#fff" stroke="#1a1a1a" strokeWidth="2.5" />

              {panel2Type.includes('confused') ? (
                // Student confused
                <g>
                  {/* Question marks */}
                  <motion.text x="60" y="60" fontSize="28" fill="#e63946"
                    animate={{ opacity: [0.4, 1, 0.4], y: [0, -5, 0] }}
                    transition={{ repeat: Infinity, duration: 2 }}
                  >?</motion.text>
                  <motion.text x="90" y="45" fontSize="24" fill="#f4a261"
                    animate={{ opacity: [0.4, 1, 0.4], y: [0, -5, 0] }}
                    transition={{ repeat: Infinity, duration: 2, delay: 0.3 }}
                  >?</motion.text>
                  <motion.text x="120" y="55" fontSize="28" fill="#e63946"
                    animate={{ opacity: [0.4, 1, 0.4], y: [0, -5, 0] }}
                    transition={{ repeat: Infinity, duration: 2, delay: 0.6 }}
                  >?</motion.text>
                  {/* Student */}
                  <circle cx="90" cy="110" r="15" fill="#fff" stroke="#1a1a1a" strokeWidth="2" />
                  <line x1="90" y1="125" x2="90" y2="165" stroke="#1a1a1a" strokeWidth="2" />
                  {/* Thought bubble */}
                  <ellipse cx="90" cy="90" rx="50" ry="25" fill="#f8f9fa" stroke="#1a1a1a" strokeWidth="1.5" />
                  <text x="90" y="95" textAnchor="middle" fontSize="14">🤔</text>
                </g>
              ) : panel2Type.includes('imbalance') ? (
                // Justice scale
                <g>
                  {/* Scale */}
                  <line x1="90" y1="40" x2="90" y2="80" stroke="#1a1a1a" strokeWidth="3" />
                  <line x1="50" y1="80" x2="130" y2="80" stroke="#1a1a1a" strokeWidth="2" />
                  {/* Left pan (lower - more weight) */}
                  <motion.g animate={{ y: [0, 3, 0] }} transition={{ repeat: Infinity, duration: 2 }}>
                    <line x1="50" y1="80" x2="40" y2="110" stroke="#1a1a1a" strokeWidth="2" />
                    <line x1="50" y1="80" x2="60" y2="110" stroke="#1a1a1a" strokeWidth="2" />
                    <ellipse cx="50" cy="120" rx="20" ry="8" fill="#e63946" opacity="0.3" />
                  </motion.g>
                  {/* Right pan (higher) */}
                  <motion.g animate={{ y: [0, -3, 0] }} transition={{ repeat: Infinity, duration: 2 }}>
                    <line x1="130" y1="80" x2="120" y2="100" stroke="#1a1a1a" strokeWidth="2" />
                    <line x1="130" y1="80" x2="140" y2="100" stroke="#1a1a1a" strokeWidth="2" />
                    <ellipse cx="130" cy="110" rx="20" ry="8" fill="#2a9d8f" opacity="0.3" />
                  </motion.g>
                  {/* Base */}
                  <rect x="70" y="180" width="40" height="15" rx="2" fill="#1a1a1a" />
                </g>
              ) : (
                <g>
                  <motion.circle cx="90" cy="90" r="30" fill="#fef3c7" stroke="#f4a261" strokeWidth="2"
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{ repeat: Infinity, duration: 2 }}
                  />
                  <text x="90" y="100" textAnchor="middle" fontSize="24">⚠️</text>
                </g>
              )}
            </svg>
            <span className={styles.panelCaption}>{sceneElements.panel2?.caption || 'Dilemma'}</span>
          </motion.div>

          {/* Panel 3 - Choices */}
          <motion.div
            className={styles.panel}
            initial={{ x: 100, opacity: 0 }}
            animate={{ x: panelStage >= 3 ? 0 : 100, opacity: panelStage >= 3 ? 1 : 0 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          >
            <svg viewBox="0 0 180 220" className={styles.panelSvg}>
              <rect x="5" y="5" width="170" height="210" rx="8" fill="#fff" stroke="#1a1a1a" strokeWidth="2.5" />
              {/* Choice cards visualization */}
              {['A', 'B', 'C', 'D'].map((label, i) => (
                <motion.g key={label}
                  initial={{ scale: 0, opacity: 0 }}
                  animate={choicesRevealed ? { scale: 1, opacity: 1 } : {}}
                  transition={{ delay: i * 0.1, type: 'spring', stiffness: 200 }}
                >
                  <rect
                    x={25 + (i % 2) * 75}
                    y={40 + Math.floor(i / 2) * 70}
                    width="60"
                    height="50"
                    rx="6"
                    fill={selected === label ? accent : '#f8f9fa'}
                    stroke={selected === label ? accent : '#1a1a1a'}
                    strokeWidth={selected === label ? 3 : 2}
                    opacity={selected && selected !== label ? 0.4 : 1}
                  />
                  <text
                    x={55 + (i % 2) * 75}
                    y={72 + Math.floor(i / 2) * 70}
                    textAnchor="middle"
                    fontSize="16"
                    fontWeight="bold"
                    fill={selected === label ? '#fff' : '#1a1a1a'}
                  >
                    {label}
                  </text>
                </motion.g>
              ))}
            </svg>
            <span className={styles.panelCaption}>Choose</span>
          </motion.div>
        </div>
      </div>

      {/* QUESTION TEXT */}
      <div className={styles.questionText}>
        {gamifiedQuestion?.situation}
      </div>

      {/* ANSWER OPTIONS - Bottom */}
      <div className={styles.optionsArea}>
        {options.map((option, index) => {
          const isSelected = selected === option.label;
          return (
            <motion.button
              key={option.label}
              type="button"
              className={`${styles.choiceBtn} ${isSelected ? styles.selected : ''}`}
              initial={{ y: 30, opacity: 0, scale: 0.9 }}
              animate={{
                y: 0,
                opacity: selected && !isSelected ? 0.4 : 1,
                scale: isSelected ? 1.02 : 1,
                x: isSelected ? -8 : 0
              }}
              transition={{ delay: 1.9 + index * 0.1, type: 'spring', stiffness: 100 }}
              whileHover={!selected ? { y: -2, borderColor: accent } : {}}
              whileTap={!selected ? { scale: 0.98 } : {}}
              onClick={() => handleSelect(option.label)}
              disabled={!!selected}
            >
              <span className={styles.choiceLabel}>{option.label}</span>
              <span className={styles.choiceText}>{option.actionText || option.text}</span>
            </motion.button>
          );
        })}
      </div>

      {/* Narration */}
      <p className={styles.narration}>{gamifiedQuestion?.narration}</p>

      <ConsequenceReveal
        visible={showOutcome}
        tone={selected === 'B' || selected === 'C' ? 'success' : 'warning'}
        message={selectedOption?.consequence || 'Decision captured.'}
        duration={1700}
        onDone={() => {
          setShowOutcome(false);
          if (selected) onAnswer(selected);
        }}
      />
    </div>
  );
};

export default DilemmaRoomEngine;
